export interface MembershipRate {
  id: string;
  period: string;
  price: number;
  rate_group_id?: string | null;
  location_id?: string | null;
  requires_approval?: boolean;
  benefits?: string[];
  benefits_alt?: string[];
  perks?: { name: string; booking_count: number; validity_days: number | null }[];
  has_perks?: boolean;
  guest_policy?: unknown;
  access_start_time?: string | null;
  access_end_time?: string | null;
  advance_booking_days?: number | null;
  fixed_slot_advance_booking_days?: number | null;
}

export function groupMembershipRates<T extends MembershipRate>(plans: T[]) {
  const groups: { key: string; plans: T[] }[] = [];
  for (const plan of plans) {
    const key = plan.rate_group_id
      ? `group:${plan.rate_group_id}`
      : `plan:${plan.id}`;
    const existing = groups.find((group) => group.key === key);
    if (existing) existing.plans.push(plan);
    else groups.push({ key, plans: [plan] });
  }
  return groups;
}

export function annualRateSavings(plans: MembershipRate[]) {
  const monthly = plans.filter((plan) => plan.period === 'monthly');
  const annual = plans.filter((plan) => plan.period === 'annual');
  if (monthly.length !== 1 || annual.length !== 1) return null;
  const month = monthly[0]!;
  const year = annual[0]!;
  const comparableFields = [
    'access_start_time',
    'access_end_time',
    'advance_booking_days',
    'fixed_slot_advance_booking_days',
    'location_id',
    'requires_approval',
  ] as const;
  if (
    comparableFields.some(
      (field) => month[field] === undefined || year[field] === undefined || month[field] !== year[field],
    )
  ) return null;
  if (
    !month.rate_group_id || month.rate_group_id !== year.rate_group_id ||
    !Number.isFinite(month.price) || !Number.isFinite(year.price) ||
    month.price <= 0 || year.price < 0
  ) return null;
  if (
    !month.benefits || !year.benefits ||
    month.guest_policy === undefined || year.guest_policy === undefined ||
    JSON.stringify(month.guest_policy) !== JSON.stringify(year.guest_policy) ||
    JSON.stringify([...month.benefits].sort()) !== JSON.stringify([...year.benefits].sort()) ||
    JSON.stringify([...(month.benefits_alt ?? [])].sort()) !== JSON.stringify([...(year.benefits_alt ?? [])].sort()) ||
    !(month.perks ? month.perks.length === 0 : month.has_perks === false) ||
    !(year.perks ? year.perks.length === 0 : year.has_perks === false)
  ) return null;
  const savings = month.price * 12 - year.price;
  return savings > 0 ? savings : null;
}

export function initialMembershipPlanId(
  plans: { id: string }[],
  requestedId?: string,
) {
  if (requestedId && plans.some((plan) => plan.id === requestedId)) return requestedId;
  return plans.length === 1 ? plans[0]!.id : '';
}
